/**
 * Locale-tab rules of the product editor (TO-416 product-admin-ui REQ-006): the
 * tab order, the draft ↔ PUT-body mapping, client limits and the dirty predicate.
 */

export interface StoredTranslation {
	locale: string
	name: string
	tagline: string | null
	shortDescription: string | null
	longDescription: string | null
	benefits: string[]
	mainCtaLabel: string | null
	secondaryCtaLabel: string | null
}

/** Editable text of one locale tab; benefits are one per line. */
export interface TranslationDraft {
	name: string
	tagline: string
	shortDescription: string
	longDescription: string
	benefits: string
	mainCtaLabel: string
	secondaryCtaLabel: string
}

export const EMPTY_DRAFT: TranslationDraft = {
	name: '',
	tagline: '',
	shortDescription: '',
	longDescription: '',
	benefits: '',
	mainCtaLabel: '',
	secondaryCtaLabel: '',
}

/** Enabled locales in stored order, then the product default, then other stored locales ascending. */
export const orderTabs = (enabled: string[], defaultLocale: string, stored: string[]): string[] => {
	const tabs = [...enabled]
	if (defaultLocale && !tabs.includes(defaultLocale)) tabs.push(defaultLocale)
	const extra = stored.filter((locale) => !tabs.includes(locale)).sort()
	return [...tabs, ...extra]
}

export const draftFromTranslation = (stored: StoredTranslation | undefined): TranslationDraft =>
	stored
		? {
				name: stored.name,
				tagline: stored.tagline ?? '',
				shortDescription: stored.shortDescription ?? '',
				longDescription: stored.longDescription ?? '',
				benefits: stored.benefits.join('\n'),
				mainCtaLabel: stored.mainCtaLabel ?? '',
				secondaryCtaLabel: stored.secondaryCtaLabel ?? '',
			}
		: EMPTY_DRAFT

const benefitLines = (text: string): string[] =>
	text
		.split('\n')
		.map((line) => line.trim())
		.filter((line) => line !== '')

const orNull = (value: string): string | null => (value === '' ? null : value)

/** The `PUT /products/{id}/translations/{locale}` body. */
export const draftToBody = (draft: TranslationDraft) => ({
	name: draft.name,
	tagline: orNull(draft.tagline),
	shortDescription: orNull(draft.shortDescription),
	longDescription: orNull(draft.longDescription),
	benefits: benefitLines(draft.benefits),
	mainCtaLabel: orNull(draft.mainCtaLabel),
	secondaryCtaLabel: orNull(draft.secondaryCtaLabel),
})

const LIMITS: Array<[keyof TranslationDraft, number, string]> = [
	['name', 255, 'translations.errors.nameLength'],
	['tagline', 255, 'translations.errors.taglineLength'],
	['shortDescription', 500, 'translations.errors.shortLength'],
	['longDescription', 10000, 'translations.errors.longLength'],
	['mainCtaLabel', 80, 'translations.errors.ctaLabelLength'],
	['secondaryCtaLabel', 80, 'translations.errors.ctaLabelLength'],
]

/** Client-side errors keyed by field; empty when the tab may be saved. */
export const translationErrors = (draft: TranslationDraft): Record<string, string> => {
	const errors: Record<string, string> = {}
	for (const [field, max, key] of LIMITS) {
		if (draft[field].length > max) errors[field] = key
	}
	if (draft.name.trim() === '') errors.name = 'translations.errors.required'
	const lines = benefitLines(draft.benefits)
	if (lines.length > 20 || lines.some((line) => line.length > 255)) errors.benefits = 'translations.errors.benefits'
	return errors
}

/** True when the draft's normalised body differs from the stored translation (or from empty). */
export const isDirty = (draft: TranslationDraft, stored: StoredTranslation | undefined): boolean =>
	JSON.stringify(draftToBody(draft)) !== JSON.stringify(draftToBody(draftFromTranslation(stored)))
